import * as React from "react";
import { useQuery } from "@tanstack/react-query";

import { API_URL, downloadBlob, downloadText, exportEmployeeTouchDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { getLastNDaysRange } from "@/lib/time";
import { useDashboardRoles } from "@/pages/dashboard/useDashboardRoles";

type PayWeekItem = { pay_week: string; from_date: string; to_date: string; label: string };

export function useTouchDetailExportViewModel() {
  const { user } = useAuth();
  const { isAdmin } = useDashboardRoles();
  const [payrollFormat, setPayrollFormat] = React.useState<"csv" | "xlsx">("csv");
  const [payrollCoCode, setPayrollCoCode] = React.useState<string[]>([]);
  const [payrollOfficeLocation, setPayrollOfficeLocation] = React.useState<string[]>([]);
  const [payrollExportError, setPayrollExportError] = React.useState<string | null>(null);
  const [payrollPayWeek, setPayrollPayWeek] = React.useState<string>("");
  const [fromDate, setFromDate] = React.useState(() => getLastNDaysRange(7).from);
  const [toDate, setToDate] = React.useState(() => getLastNDaysRange(7).to);
  const [payWeekDefaultApplied, setPayWeekDefaultApplied] = React.useState(false);
  const currentYear = React.useMemo(() => new Date().getFullYear(), []);

  const { data: payrollCoCodesData, isLoading: payrollCoCodesLoading, error: payrollCoCodesError } = useQuery({
    queryKey: ["payroll-co-codes"],
    queryFn: async () => {
      const resp = await fetch(`${API_URL}/entries/co-codes`, { credentials: "include" });
      if (!resp.ok) return { co_codes: [] as string[], companies: {} as Record<string, string> };
      return resp.json() as Promise<{ co_codes: string[]; companies?: Record<string, string> }>;
    },
    enabled: !!isAdmin,
  });
  const { data: officeLocationsData, isLoading: officeLocationsLoading, error: officeLocationsError } = useQuery({
    queryKey: ["office-locations"],
    queryFn: async () => {
      const resp = await fetch(`${API_URL}/entries/office-locations`, { credentials: "include" });
      if (!resp.ok) return { office_locations: [] as string[] };
      return resp.json() as Promise<{ office_locations: string[] }>;
    },
    enabled: !!isAdmin,
  });
  const { data: payWeeksData, isLoading: payWeeksLoading, error: payWeeksError } = useQuery({
    queryKey: ["pay-weeks", currentYear],
    queryFn: async () => {
      const resp = await fetch(`${API_URL}/entries/pay-weeks?year=${currentYear}`, { credentials: "include" });
      if (!resp.ok) return { pay_weeks: [] as PayWeekItem[], current: null as PayWeekItem | null };
      return resp.json() as Promise<{ pay_weeks: PayWeekItem[]; current: PayWeekItem | null }>;
    },
    enabled: !!user,
  });
  React.useEffect(() => {
    if (payWeekDefaultApplied || !payWeeksData?.current) return;
    const c = payWeeksData.current;
    setPayrollPayWeek(c.pay_week);
    setFromDate(c.from_date);
    setToDate(c.to_date);
    setPayWeekDefaultApplied(true);
  }, [payWeeksData, payWeekDefaultApplied]);

  return {
    isAdmin,
    payrollFormat, setPayrollFormat,
    payrollCoCode, setPayrollCoCode,
    payrollOfficeLocation, setPayrollOfficeLocation,
    payrollExportError, setPayrollExportError,
    payrollPayWeek, setPayrollPayWeek,
    fromDate, setFromDate,
    toDate, setToDate,
    payrollCoCodesData, payrollCoCodesLoading, payrollCoCodesError,
    officeLocationsData, officeLocationsLoading, officeLocationsError,
    payWeeksData, payWeeksLoading, payWeeksError,
    downloadBlob, downloadText, exportEmployeeTouchDetail,
  };
}
